import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import DownArrow from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useField } from 'formik';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import { important, isNil } from '../../../../utils/global/helper';
import InputWrapper from '../InputWrapper';
import { getInputBorderStyle } from '../../../../helpers/styles';

interface Props {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  options: {
    icon?: SvgIconComponent;
    category: string;
    value: string | boolean | number;
    label: string;
    tooltip?: string;
    onClick?: () => void;
  }[];
  tooltip?: string;
  autoWidth?: boolean;
  p?: number;
  required?: boolean;
  selectedBackgroundColor?: string;
  selectedCheckColor?: string;
}

function SelectOption({
  name,
  option,
  onClick: onClickProp,
  p = 2,
  selectedBackgroundColor,
  selectedCheckColor,
}: {
  name: string;
  option: Props['options'][number];
  onClick?: () => void;
  p?: number;
  selectedBackgroundColor?: string;
  selectedCheckColor?: string;
}) {
  const theme = useTheme();
  const [, , { setTouched }] = useField(name);

  const onTouched = () => {
    setTouched?.(true);
  };

  const [{ value }, , { setValue }] = useField(name);
  const onClick = () => {
    onClickProp?.();
    onTouched();
    if (option.onClick) {
      option.onClick();
      return;
    }
    setValue(option.value as string);
  };

  const isSelected = value === option.value;

  const Icon = option.icon;

  return (
    <MenuItem
      sx={{
        p,
        fontSize: '0.875rem',
        backgroundColor: isSelected
          ? selectedBackgroundColor || theme.palette.accent.main
          : undefined,
        ':hover': {
          backgroundColor: isSelected
            ? selectedBackgroundColor || theme.palette.accent.main
            : theme.palette.lightGrey.main,
        },
      }}
      onClick={onClick}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%">
        <Box display="flex" alignItems="center" gap={1}>
          {Icon && (
            <Icon
              fontSize="large"
              sx={{
                width: '16px',
                height: '16px',
              }}
              htmlColor={theme.palette.darkGrey.main}
            />
          )}
          <Typography variant="inherit" color={theme.palette.darkGrey.main}>
            {option.label}
          </Typography>
          {option.tooltip && (
            <Tooltip
              title={option.tooltip}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -5],
                      },
                    },
                  ],
                },
              }}
              enterTouchDelay={0}>
              <Button
                variant="text"
                color="inherit"
                sx={{
                  padding: 0,
                  border: 'none',
                  width: 'auto',
                  minWidth: 0,
                  ':hover': {
                    backgroundColor: 'transparent',
                  },
                }}>
                <InfoOutlinedIcon sx={{ fontSize: 12 }} fontSize="small" />
              </Button>
            </Tooltip>
          )}
        </Box>
        {isSelected && (
          <CheckIcon
            sx={{
              width: '16px',
              height: '16px',
            }}
            htmlColor={selectedCheckColor || theme.palette.secondary.main}
          />
        )}
      </Box>
    </MenuItem>
  );
}

interface Category {
  category: string;
  options: Props['options'];
}

function CategorySingleSelect({
  name: nameProp,
  label,
  placeholder,
  disabled,
  options,
  tooltip,
  autoWidth = true,
  p = 2,
  required,
  selectedBackgroundColor,
  selectedCheckColor,
}: Props) {
  const name = nameProp.toString();

  // only way to close menu since we use custom options
  const [key, setKey] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [{ value }, { error, touched }, { setValue, setTouched }] =
    useField(name);

  const onChange = (event: SelectChangeEvent<string>) => {
    setValue(event.target.value as string);
    setTimeout(() => setTouched(true), 0);
  };

  const categories = options.reduce<Category[]>((acc, option) => {
    if (isNil(option.category)) return acc;
    const existing = acc.find(cat => cat.category === option.category);
    if (existing) {
      existing.options.push(option);
    } else {
      acc.push({ category: option.category, options: [option] });
    }
    return acc;
  }, []);

  const allOptions = categories.flatMap(category => category.options);

  const isError = !!error && touched;

  return (
    <InputWrapper
      label={label}
      name={name}
      tooltip={tooltip}
      required={required}>
      <Select
        key={key}
        IconComponent={DownArrow}
        MenuProps={{
          open,
          sx: {
            '.MuiList-root': {
              padding: 0,
            },
          },
        }}
        sx={theme => ({
          height: important('44px'),
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': getInputBorderStyle({
            theme,
            isError,
          }),
        })}
        defaultValue={value || ''}
        value={value || ''}
        onChange={onChange}
        input={<OutlinedInput />}
        displayEmpty
        autoWidth={autoWidth}
        label={placeholder}
        disabled={disabled}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onBlur={() => {
          setTouched(true);
        }}>
        {placeholder && (
          <MenuItem
            sx={{
              fontSize: '0.875rem',
            }}
            disabled
            value="">
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {/* To suppress MUI warnings */}
        {allOptions.map(option => (
          <MenuItem
            key={option.value.toString()}
            value={option.value as string}
            sx={{
              display: 'none',
            }}>
            {option.label}
          </MenuItem>
        ))}

        {categories.flatMap((category, categoryIndex) =>
          category.options.flatMap((option, index) => (
            <Box key={(option.category || '') + option.value.toString()}>
              {index === 0 && !!option.category && (
                <Box>
                  {categoryIndex > 0 && (
                    <Divider
                      sx={theme => ({
                        backgroundColor: theme.palette.lightGrey.main,
                      })}
                    />
                  )}
                  <Typography
                    p={p}
                    variant="h4"
                    textTransform="uppercase"
                    sx={{ color: theme => theme.palette.medGrey.main }}>
                    {category.category}
                  </Typography>
                  <Divider
                    sx={theme => ({
                      backgroundColor: theme.palette.lightGrey.main,
                    })}
                  />
                </Box>
              )}
              <SelectOption
                name={name}
                option={option}
                onClick={() => {
                  setOpen(false);
                  setKey(Date.now());
                }}
                p={p}
                selectedBackgroundColor={selectedBackgroundColor}
                selectedCheckColor={selectedCheckColor}
              />
            </Box>
          )),
        )}
      </Select>
    </InputWrapper>
  );
}

export default CategorySingleSelect;
